/* eslint-disable react/jsx-one-expression-per-line */
import { Flex, Box, Stack, Badge, Text } from '@chakra-ui/core';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { RemoteConfigKey } from '../../../../../firebase/remoteConfig/keys';
import { useStoreState } from '../../../../../models/hooks';
import { PositionState } from '../../../../common';
import { useGetCandidatePositionNextStatus } from './candidatePositionNextStatus/useGetCandidatePositionNextStatus';
import CandidatePickerItemPresenter from './item/candidatePickerItemPresenter';
import useGetCandidatePositionStatus from './useGetCandiatePositionStatus';

export const CanidateActionAndNextAction = memo(function CanidateActionAndNextAction({
  candidate,
  secondaryTextColor,
}: {
  candidate: CandidatePickerItemPresenter | null;
  secondaryTextColor: string;
}) {
  const { t } = useTranslation('candidateNextStatus');
  const getStatus = useGetCandidatePositionStatus(candidate);
  const getNextStatus = useGetCandidatePositionNextStatus(candidate);
  const nextStatus = getNextStatus();
  const isCandidateNextActionRemoteConfigDisabled = useStoreState(
    (state) => state.remoteConfig[RemoteConfigKey.DISABLE_CANDIDATE_NEXT_ACTION],
  );

  const showNextAction = !isCandidateNextActionRemoteConfigDisabled && !!nextStatus;

  if (!candidate) {
    return null;
  }

  return (
    <Flex alignItems="center">
      <Box maxW="360px">
        <Flex position="relative">
          <Box position="relative">
            {showNextAction && (
              <Box
                as="svg"
                position="absolute"
                left="0"
                top="0"
                width="20px"
                height="40px"
                viewBox="0 0 20 40"
                fill="none"
                zIndex={2}
              >
                <defs>
                  <marker
                    id="arrowhead"
                    viewBox="0 0 10 10"
                    markerWidth="7"
                    markerHeight="7"
                    refX="8"
                    refY="5"
                    markerUnits="userSpaceOnUse"
                    orient="auto"
                  >
                    <path d="M0,0 L10,5 L0,10 Z" fill={nextStatus?.colorScheme.color} />
                  </marker>
                </defs>

                <path
                  d="M5 16 L5 24 Q5 32 18 32 L20 32"
                  stroke={nextStatus?.colorScheme.color}
                  strokeWidth="2"
                  markerEnd="url(#arrowhead)"
                />
              </Box>
            )}

            <Box zIndex={2} marginTop={1}>
              <PositionState status={candidate.status} isDeactivated={candidate?.candidate.deactivated} />
            </Box>
          </Box>

          <Stack spacing={0} ml={showNextAction ? 2 : 0} alignItems="flex-start">
            <Text
              fontSize="xs"
              color={secondaryTextColor}
              lineHeight="shorter"
              pt={1}
              maxW={{ base: '11rem', md: '12rem', lg: '18rem' }}
              mb={showNextAction ? 1 : 0}
            >
              {getStatus()}
            </Text>
            {showNextAction && (
              <Badge
                size="sm"
                colorScheme={nextStatus?.colorScheme.colorScheme}
                textTransform="none"
                fontWeight="normal"
                fontSize="xs"
              >
                <Text as="div" color="#607D8B">
                  {t('nextAction')}:
                  <Text as="span" lineHeight="shorter" pt={1} pl={2}>
                    {t(nextStatus?.nextAction)}
                  </Text>
                </Text>
              </Badge>
            )}
          </Stack>
        </Flex>
      </Box>
    </Flex>
  );
});
